import React, { FC, useCallback, useState } from "react";
import classNames, { Argument } from "classnames/dedupe";
import { CSSTransition } from "react-transition-group";

import {
  Divider,
  DropdownDrawingStrategy,
  HeaderNavSection,
  Link,
  MainHeader,
  MainHeaderContainer,
  NavItem,
  NavList,
  SearchContext,
  USPBanner,
  PromoLine,
  useSearchState,
} from "../../components";

import { SvgIcon } from "../../components/Icons";
import { MainLogo } from "../../icons";
import DMBrandsLogoFlyout from "./FlyoutMenus/DMBrandsLogoFlyout";
import DMSportsFlyout from "./FlyoutMenus/DMSportsFlyout";
import useScrollDir from "../../hooks/useScrollDir";
import HeaderSearch from "../../components/Navigational/Search/HeaderSearch/HeaderSearch";
import {
  mockEmptyServerResponse,
  mockServerResponse,
} from "../../mock/searchData";
import useWindowDimensions from "../../hooks/useWindowDimensions";
import { GRID_BREAKPOINTS } from "../../components/Fundamentals/Grid/Column";
import { mockedGenderOptions } from "../../mock/gender";
import { MenuIcons } from "./MenuIcons";

export interface IDesktopMenu {
  /**
   * The custom set of css classes used to style component
   *
   * root: applied to outer 'div' element
   */
  classes?: { banner?: Argument; menu?: Argument };
}

const DesktopMenu: FC<IDesktopMenu> = ({ classes }) => {
  const MENU_HEIGHT = 52;
  const DIVIDER_HEIGHT = 4;
  const dir = useScrollDir();

  const [selectedGender, setSelectedGender] = useState<string>("");

  const [menuExited, setMenuExited] = useState(false);
  const handleMenuEntered = () => setMenuExited(false);
  const handleMenuExited = () => setMenuExited(true);

  const [mockResponse, setMockResponse] = useState(mockEmptyServerResponse);

  const handleSearch = useCallback((searchTerm: string) => {
    if (searchTerm) setMockResponse(mockServerResponse);
    else setMockResponse(mockEmptyServerResponse);
  }, []);
  const context = useSearchState({
    onSearch: handleSearch,
  });

  const viewportSizes = useWindowDimensions();
  const isMobileSearch = viewportSizes.viewportWidth < GRID_BREAKPOINTS.lgm;

  const onHeaderClear = useCallback(() => {
    setMockResponse(mockEmptyServerResponse);
  }, []);

  return (
    <SearchContext.Provider value={context}>
      <USPBanner
        classes={{ root: classes?.banner }}
        shippingLabel="1–5 working Days"
        returnLabel="FREE Returns & Exchanges"
        pocketLabel="save 2.5% on all purchases"
      />
      <div
        className={classNames(
          "bg-color-white position-sticky container-fluid header-padding-placeholder-main",
          classes?.menu,
        )}
        style={{ top: dir === "down" ? `-${MENU_HEIGHT}px` : 0 }}
        id="main-menu-header"
      >
        <MainHeader>
          <MainHeaderContainer variant="center">
            <Link
              classes={{
                root: classNames("header-brand", {
                  "d-xl-block": menuExited && context.isActive,
                  "d-none": !isMobileSearch || context.isActive,
                  "d-lgm-block": !(menuExited && context.isActive),
                }),
              }}
              href="/"
            >
              <>
                <SvgIcon
                  icon={MainLogo}
                  isIcon={false}
                  width={155}
                  height={16}
                  classes={{ root: "header-brand__desktop" }}
                />
                <SvgIcon
                  icon={MainLogo}
                  isIcon={false}
                  width={116}
                  height={13}
                  classes={{ root: "header-brand__mobile" }}
                />
              </>
            </Link>

            <CSSTransition
              classNames="uds-appear-fade"
              in={!context.isActive}
              timeout={100}
              mountOnEnter
              unmountOnExit
              onEnter={handleMenuEntered}
              onExited={handleMenuExited}
            >
              <HeaderNavSection variant="list">
                <NavList ariaLabel="Gender Menu">
                  {mockedGenderOptions.map((genderOption, index) => {
                    const isCurrent = selectedGender === genderOption.id;
                    const handleGenderSelection = () =>
                      setSelectedGender(isCurrent ? "" : genderOption.id);
                    const isLast = mockedGenderOptions.length - 1 === index;
                    const { content: Content } = genderOption;
                    return (
                      <NavItem
                        key={genderOption.id}
                        classes={{
                          root: isLast ? "" : "me-4 me-xl-5",
                          wrapper: "pt-6",
                        }}
                        text={genderOption.text}
                        drawingStrategy={DropdownDrawingStrategy.CSS}
                        maskOffset={{ top: "20" }}
                        dropdownProps={{
                          classes: {
                            wrapper: {
                              "position-relative": false,
                            },
                          },
                        }}
                        dropdownListProps={{
                          classes: { root: "mt-n6 uds-dropdown-toggle" },
                        }}
                        active={isCurrent}
                        onClick={handleGenderSelection}
                      >
                        <Content />
                      </NavItem>
                    );
                  })}
                </NavList>

                <NavList ariaLabel="Add menu">
                  <NavItem
                    text="SPORTS & EQUIPMENT"
                    type="light"
                    classes={{
                      root: "white-space-nowrap me-4 me-xl-5",
                      wrapper: "pt-6",
                    }}
                    drawingStrategy={DropdownDrawingStrategy.CSS}
                    maskOffset={{ top: "20" }}
                    dropdownProps={{
                      classes: {
                        wrapper: {
                          "position-relative": false,
                        },
                      },
                    }}
                    dropdownListProps={{
                      classes: { root: "mt-n6 uds-dropdown-toggle" },
                    }}
                  >
                    <DMSportsFlyout />
                  </NavItem>
                  <NavItem
                    text="BRANDS"
                    type="light"
                    classes={{
                      root: "white-space-nowrap me-4 me-xl-5",
                      wrapper: "pt-6",
                    }}
                    drawingStrategy={DropdownDrawingStrategy.CSS}
                    maskOffset={{ top: "20" }}
                    dropdownProps={{
                      classes: {
                        wrapper: {
                          "position-relative": false,
                        },
                      },
                    }}
                    dropdownListProps={{
                      classes: { root: "mt-n6 uds-dropdown-toggle" },
                    }}
                  >
                    <DMBrandsLogoFlyout />
                  </NavItem>
                  <NavItem
                    text="FITNESS"
                    type="light"
                    classes={{ root: "white-space-nowrap", wrapper: "pt-6" }}
                    drawingStrategy={DropdownDrawingStrategy.CSS}
                    maskOffset={{ top: "20" }}
                    dropdownProps={{
                      classes: {
                        wrapper: {
                          "position-relative": false,
                        },
                      },
                    }}
                    dropdownListProps={{
                      classes: { root: "mt-n6 uds-dropdown-toggle" },
                    }}
                  >
                    <div>Fitness tab content</div>
                  </NavItem>
                </NavList>
              </HeaderNavSection>
            </CSSTransition>

            <HeaderNavSection
              classes={{ root: "" }}
              variant={context.isActive ? "searchFocused" : "search"}
            >
              <HeaderSearch
                searchResults={mockResponse}
                onSearch={handleSearch}
                onClear={onHeaderClear}
                withDetails
                minSearchTermLength={0}
                menuHeight={MENU_HEIGHT}
                menuID="main-menu-header"
                opened={context.isActive}
              />
            </HeaderNavSection>
            <CSSTransition
              classNames="uds-appear-fade"
              in={!context.isActive}
              timeout={300}
              mountOnEnter
              unmountOnExit={false}
            >
              <HeaderNavSection
                variant="icons"
                classes={{
                  root: classNames(`header-icons-base`, {
                    "fade-out": context.isActive,
                    "d-none d-xl-block": !isMobileSearch
                      ? menuExited && context.isActive
                      : context.isActive,
                  }),
                }}
              >
                <MenuIcons />
              </HeaderNavSection>
            </CSSTransition>
          </MainHeaderContainer>
        </MainHeader>
      </div>
      <div
        className="header-padding-placeholder-divider container-fluid bg-color-transparent position-sticky"
        style={{
          top: dir === "down" ? `-${DIVIDER_HEIGHT}px` : `${MENU_HEIGHT}px`,
        }}
      >
        <Divider size="bold" />
      </div>
      <PromoLine
        title="Save 25% On All Wintersport Apparel. See"
        highlight="The Lastest Sales"
        link={{ href: "/" }}
      />
    </SearchContext.Provider>
  );
};

export default DesktopMenu;
